import { useEffect, useState } from "react";
import { Loader2, Save, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const SECTIONS = [
  {
    key: "email_marketing",
    title: "Email marketing provider",
    hint: "Future newsletter platform (for example ConvertKit or Mailchimp). Credentials are stored on the server only.",
    fields: ["provider", "api_key"],
  },
  {
    key: "transactional_email",
    title: "Transactional email",
    hint: "Managed Resend connection used for report and reminder emails.",
    fields: ["provider", "note"],
  },
  {
    key: "scheduling",
    title: "Consultation scheduling",
    hint: "External scheduler link. Deeper API or webhook connection can be configured later.",
    fields: ["provider", "booking_url"],
  },
  {
    key: "analytics",
    title: "Approved analytics",
    hint: "Platform analytics plus the internal non-sensitive event log. Never send wellness answers to analytics.",
    fields: ["provider", "note"],
  },
];

export default function AdminIntegrations() {
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);

  const load = () => api.get("/admin/integrations").then(({ data }) => setData(data));

  useEffect(() => {
    document.title = "Integrations | Rooted Healing Admin";
    load();
  }, []);

  const save = async (section) => {
    setSaving(section);
    try {
      await api.put("/admin/integrations", { section, values: drafts[section] || {} });
      toast.success("Integration settings saved on the server.");
      setDrafts((d) => ({ ...d, [section]: {} }));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(null);
  };

  if (!data) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-7 w-7 animate-spin text-forest" />
      </div>
    );
  }

  return (
    <div data-testid="admin-integrations">
      <h1 className="font-serif text-3xl text-forest">Integrations</h1>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-taupe">
        API credentials are stored server-side only and are masked after saving. They never
        appear in browser-accessible source code.
      </p>
      <div className="mt-8 grid grid-cols-1 gap-5 lg:grid-cols-2">
        {SECTIONS.map((s) => (
          <div
            key={s.key}
            data-testid={`integration-${s.key}`}
            className="rounded-[1.75rem] border border-forest/10 bg-white/75 p-7"
          >
            <div className="flex items-center justify-between gap-3">
              <h2 className="font-serif text-xl text-forest">{s.title}</h2>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-sage px-3 py-1 text-[11px] font-semibold text-forest">
                <ShieldCheck className="h-3 w-3" />
                {data.status[s.key]}
              </span>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-taupe">{s.hint}</p>
            <div className="mt-4 flex flex-col gap-3">
              {s.fields.map((f) => (
                <div key={f}>
                  <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.15em] text-forest">
                    {f.replace(/_/g, " ")}
                  </label>
                  <input
                    data-testid={`integration-${s.key}-${f}`}
                    type={f.includes("key") || f.includes("token") ? "password" : "text"}
                    placeholder={data.sections?.[s.key]?.[f] ? String(data.sections[s.key][f]) : ""}
                    value={drafts[s.key]?.[f] || ""}
                    onChange={(e) =>
                      setDrafts((d) => ({
                        ...d,
                        [s.key]: { ...(d[s.key] || {}), [f]: e.target.value },
                      }))
                    }
                    className="w-full rounded-full border border-forest/20 bg-white/70 px-5 py-3 text-sm outline-none focus:border-gold"
                  />
                </div>
              ))}
            </div>
            <button
              data-testid={`integration-save-${s.key}`}
              onClick={() => save(s.key)}
              disabled={saving === s.key || !Object.keys(drafts[s.key] || {}).length}
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-forest px-6 py-3 text-sm font-semibold text-ivory transition-colors hover:bg-forest-deep disabled:opacity-40"
            >
              {saving === s.key ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
